import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { api } from "../lib/api";
import { Button, ErrorNote } from "./primitives";
import { Mark } from "./Mark";

/**
 * The beta terms, in front of the app until they are accepted.
 *
 * **This is the notice, not the enforcement.** `api/auth/middleware.py` refuses
 * every `/api` route with `code: "disclaimer"` until the acceptance is
 * recorded, and that is what makes it a control — a modal is dismissible with
 * devtools and bypassable by calling the API directly. Same relationship as
 * `RequireAuth` and the 401.
 *
 * The wording comes from the server so that the version the acceptance names
 * identifies a particular text. Bumping `disclaimer.VERSION` brings this back
 * for everyone, which is the point: consent to the old wording is not consent
 * to the new.
 */
export function DisclaimerGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });

  const accept = useMutation({
    mutationFn: api.acceptDisclaimer,
    onSuccess: () => {
      // Everything was being refused until this moment, so anything already
      // fetched is an error state rather than data.
      queryClient.invalidateQueries();
    },
  });

  if (me.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Mark size={44} animate loop />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  // An error here is not the disclaimer's business — `RequireAuth` above has
  // already decided the session is good, and blocking on a failed /api/me
  // would put an unexplained wall in front of a working app.
  const user = me.data;
  const terms = user?.disclaimer;
  if (!user || !terms || user.disclaimer_version === terms.version) {
    return <>{children}</>;
  }

  return (
    // No dismiss, no escape, no backdrop click: there is one way past this and
    // it is the button. `role="alertdialog"` is what tells a screen reader the
    // same thing.
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="disclaimer-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-plane/90 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-surface shadow-lg">
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <Mark size={24} />
          <h1 id="disclaimer-title" className="text-base font-semibold text-ink">
            {terms.title}
          </h1>
        </div>

        <div className="space-y-4 px-5 py-5 text-sm text-ink-2">
          <p>{terms.body}</p>

          <div>
            <p className="font-medium text-ink">Do not upload:</p>
            <ul className="mt-1.5 space-y-1">
              {terms.prohibited.map((line) => (
                <li key={line} className="flex gap-2">
                  {/* A rule, not a warning: three orange dashes on one card
                      were three things asking for attention at once (#124). */}
                  <span aria-hidden className="text-baseline">
                    —
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Why the list above follows. Both are true whatever the user does
              and neither is visible from the interface. */}
          <div className="space-y-1.5 rounded-lg bg-surface-2 px-4 py-3 text-xs">
            {terms.because.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>

        {accept.isError && (
          <div className="px-5 pb-2">
            {/* Said out loud rather than silently retried: if the write fails,
                a modal that closed anyway would show again next time and read
                as a bug. */}
            <ErrorNote error={accept.error} />
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-4">
          <button
            type="button"
            onClick={() => api.logout().then(() => window.location.assign("/login"))}
            className="text-xs text-muted underline hover:text-ink"
          >
            Sign out instead
          </button>
          <Button
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            autoFocus
          >
            {accept.isPending ? "Recording…" : terms.confirm}
          </Button>
        </div>
      </div>
    </div>
  );
}
